import { useState, useMemo, useCallback } from 'react';
import { Goal, PriorityType } from '../context/GoalsContext';
import {
  GoalProgressStatus,
  getGoalProgressStatus,
  goalMatchesSearchQuery,
} from '../utils/goalProgressStatus';

export type GoalTypeFilter = Goal['type'] | 'all';
export type LoanTypeFilter = 'credit-card' | 'fixed-term' | 'all';
export type GoalStatusFilter = GoalProgressStatus | 'all';

export function useGoalFilters(goals: Goal[]) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<GoalTypeFilter>('all');
  const [selectedPriorityFilter, setSelectedPriorityFilter] = useState<PriorityType | 'all'>('all');
  const [selectedLoanTypeFilter, setSelectedLoanTypeFilter] = useState<LoanTypeFilter>('all');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<GoalStatusFilter>('all');
  const [showFilters, setShowFilters] = useState(false);

  const filteredGoals = useMemo(() => {
    return goals.filter(goal => {
      const matchesSearch = goalMatchesSearchQuery(goal, searchQuery);

      const matchesType = selectedTypeFilter === 'all' || goal.type === selectedTypeFilter;

      const matchesPriority =
        selectedPriorityFilter === 'all' || goal.priority === selectedPriorityFilter;

      const matchesStatus =
        selectedStatusFilter === 'all' || getGoalProgressStatus(goal) === selectedStatusFilter;

      let matchesLoanType = true;
      if (selectedLoanTypeFilter !== 'all' && goal.type === 'Debt') {
        if (selectedLoanTypeFilter === 'credit-card') {
          matchesLoanType = goal.loanTermYears === undefined;
        } else if (selectedLoanTypeFilter === 'fixed-term') {
          matchesLoanType = goal.loanTermYears !== undefined;
        }
      } else if (selectedLoanTypeFilter !== 'all') {
        matchesLoanType = false;
      }

      return matchesSearch && matchesType && matchesPriority && matchesStatus && matchesLoanType;
    });
  }, [
    goals,
    searchQuery,
    selectedTypeFilter,
    selectedPriorityFilter,
    selectedLoanTypeFilter,
    selectedStatusFilter,
  ]);

  const hasActiveFilters =
    searchQuery !== '' ||
    selectedTypeFilter !== 'all' ||
    selectedPriorityFilter !== 'all' ||
    selectedLoanTypeFilter !== 'all' ||
    selectedStatusFilter !== 'all';

  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedTypeFilter('all');
    setSelectedPriorityFilter('all');
    setSelectedLoanTypeFilter('all');
    setSelectedStatusFilter('all');
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    selectedTypeFilter,
    setSelectedTypeFilter,
    selectedPriorityFilter,
    setSelectedPriorityFilter,
    selectedLoanTypeFilter,
    setSelectedLoanTypeFilter,
    selectedStatusFilter,
    setSelectedStatusFilter,
    showFilters,
    setShowFilters,
    filteredGoals,
    hasActiveFilters,
    clearFilters,
  };
}
